import React from "react";
import { Redirect } from "react-router-dom";
import { Input } from "semantic-ui-react";
import styled from "styled-components";
import { Paths } from "../Constants";
import dateTimeHelper from "../util/dateTimeHelper";
import { isDobFormatValid } from "../util/regexHelper";
import stringHelper from "../util/stringHelper";
import BaseComponent from "./BaseComponent";
import AppHeaderSmall from "./common/AppHeaderSmall";
import Footer from "./common/Footer";
import MainContentWrapper from "./common/MainContentWrapper";
import {
  default as ErrorDisplay,
  default as PillButton,
} from "./common/PillButton";
import SFCookieConsent from "./common/SFCookieConsent";
import SiteWrapper from "./common/SiteWrapper";
import TabBar from "./common/TabBar";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: row;
  margin-top: 10px;
`;

const AppText = styled.div``;

class Me extends BaseComponent {
  constructor() {
    super();
    this.state = {
      useBarcode: false,
      barcodeRead: false,
      pendingChanges: false,
      identityUserId: null,
      firstName: null,
      lastName: null,
      dob: null,
      postalCode: null,
      editMode: false,
      validTestCount: 1,
      originalData: null,
      showActivityIndicator: false,
    };

    this.completeRegistration = this.save.bind(this);
    this.switchEditingMode = this.switchEditingMode.bind(this);
    this.setData = this.setData.bind(this);
    this.checkForm = this.checkForm.bind(this);
    this.isFormValid = this.isFormValid.bind(this);
  }

  switchEditingMode() {
    if (this.state.editMode) {
      this.errorManager.reset();
      this.setData(this.state.originalData);
    }
    this.setState({ editMode: !this.state.editMode });
  }

  async componentDidMount() {
    if (!(await this.checkLogin())) {
      return;
    }
    this.setState({ showActivityIndicator: true });
    const result = await this.safeFunApi.getCurrentUser();
    const consumer = result.data;
    this.setData(consumer);
    this.setState({ showActivityIndicator: false });
  }

  setData(consumer) {
    this.setState({ originalData: consumer });
    this.setState({ identityUserId: consumer.identityUserId });
    this.setState({ firstName: consumer.firstName });
    this.setState({ middleName: consumer.middleName });
    this.setState({ lastName: consumer.lastName });
    this.setState({ dob: dateTimeHelper.formatDate(consumer.dob) });
    this.setState({ postalCode: consumer.postalCode });
  }

  async save() {
    if (!(await this.checkLogin())) {
      return;
    }

    this.setState({ showActivityIndicator: true });
    const consumer = {
      identityUserId: encodeURIComponent(this.state.identityUserId),
      firstName: encodeURIComponent(this.state.firstName),
      middleName: stringHelper.isNullOrEmpty(this.state.middleName)
        ? null
        : encodeURIComponent(this.state.middleName),
      lastName: encodeURIComponent(this.state.lastName),
      dob: encodeURIComponent(
        dateTimeHelper.formattedDateToDbDate(this.state.dob)
      ),
      postalCode: encodeURIComponent(this.state.postalCode),
    };

    try {
      await this.safeFunApi.saveUser(consumer);

      //set original to the new data after successful save
      this.setState({
        originalData: consumer,
        pendingChanges: false,
        editMode: false,
      });

      this.setState({ showActivityIndicator: false });
      this.redirect(Paths.SETTINGS);
    } catch (err) {
      this.errorManager.addError(
        "There was a problem saving your profile information."
      );
      this.setState({ showActivityIndicator: false });
    }
  }

  checkForm() {
    this.setState({ formIsValid: this.isFormValid() });
  }

  isFormValid() {
    this.errorManager.reset();
    if (
      stringHelper.isNullOrEmpty(this.state.firstName) ||
      stringHelper.isNullOrEmpty(this.state.lastName) ||
      stringHelper.isNullOrEmpty(this.state.dob) ||
      stringHelper.isNullOrEmpty(this.state.postalCode)
    ) {
      return false;
    } else {
      //something in there for everything. Start validating.

      //DOB check

      if (this.state.dob !== null && this.state.dob !== "") {
        if (!isDobFormatValid(this.state.dob)) {
          this.errorManager.addError(
            "Date of Birth must be in mm/dd/yyyy format"
          );
          this.forceUpdate();
          return;
        }

        const dbDate = dateTimeHelper.formattedDateToDbDate(this.state.dob);
        if (!dateTimeHelper.isDBDateOverAge(dbDate, 18)) {
          this.errorManager.addError(
            "Users must be at least 18 years of age to use SafeFun"
          );
          this.setState({ isOver18: false });
          return false;
        } else {
          this.setState({ isOver18: true });
        }
      }
      return true;
    }
  }

  render() {
    return (
      <SiteWrapper>
        {this.state.redirectPath ? (
          <Redirect
            to={{
              pathname: this.state.redirectPath,
              search: this.state.redirectSearch,
              state: this.state.redirectState,
            }}
          />
        ) : null}

        <AppHeaderSmall title="Personal Info" />
        <MainContentWrapper>
          <TabBar settings />

          <Wrapper>
            <Section style={{ marginTop: 20 }}>
              <Input
                style={{ width: "60%" }}
                placeholder="First Name*"
                autoCapitalize="none"
                autoCorrect={false}
                value={this.state.firstName}
                disabled={!this.state.editMode}
                onBlur={this.checkForm}
                onChange={(e) =>
                  this.setState({
                    firstName: e.target.value,
                    pendingChanges: true,
                  })
                }
              />
              <Input
                style={{ width: "60%" }}
                placeholder="Last Name*"
                autoCapitalize="none"
                autoCorrect={false}
                value={this.state.lastName}
                disabled={!this.state.editMode}
                onBlur={this.checkForm}
                onChange={(e) =>
                  this.setState({
                    lastName: e.target.value,
                    pendingChanges: true,
                  })
                }
              />
              <Input
                style={{ width: "60%" }}
                placeholder="Date of Birth* (MM/DD/YYYY)"
                autoCapitalize="none"
                autoCorrect={false}
                value={this.state.dob}
                disabled={!this.state.editMode}
                onBlur={this.checkForm}
                onChange={(e) =>
                  this.setState({ dob: e.target.value, pendingChanges: true })
                }
              />
              <Input
                style={{ width: "60%" }}
                placeholder="Postal Code*"
                autoCapitalize="none"
                autoCorrect={false}
                value={this.state.postalCode}
                disabled={!this.state.editMode}
                onBlur={this.checkForm}
                onChange={(e) =>
                  this.setState({
                    postalCode: e.target.value,
                    pendingChanges: true,
                  })
                }
              />
            </Section>
            {this.state.validTestCount > 0 ? (
              <AppText
                style={{ marginLeft: 30, marginRight: 30, marginBottom: 20 }}
              >
                Changing any of your personal information on this screen can
                invalidate existing, verified tests that used this information
                to perform a valid match
              </AppText>
            ) : null}
            {this.state.showActivityIndicator ? <></> : null}
            {this.errorManager.getErrorCount() > 0 ? (
              <ErrorDisplay
                style={{
                  marginLeft: 30,
                  marginRight: 30,
                  marginBottom: 20,
                  color: "red",
                }}
                errorManager={this.errorManager}
              />
            ) : null}

            <ButtonGroup>
              <PillButton
                style={{ width: "150px" }}
                primary
                onClick={() => {
                  this.switchEditingMode();
                }}
              >
                {this.state.editMode ? "Cancel" : "Change Your Info"}
              </PillButton>
              <PillButton
                style={{ width: "150px" }}
                primary
                onClick={() => {
                  this.save();
                }}
                disabled={
                  !this.state.formIsValid ||
                  !this.state.isOver18 ||
                  this.state.showActivityIndicator
                }
              >
                Save
              </PillButton>
            </ButtonGroup>
          </Wrapper>
        </MainContentWrapper>
        <Footer />
        <SFCookieConsent />
      </SiteWrapper>
    );
  }
}

export default Me;
